// The verification view's two panes (TGCLOUD-1868, plan D2): the report on the left, the Test on
// the right.
//
//   1200px and wider   side by side, half each (1366 gives about 683px), each scrolling on its own
//                      and filling the height it is given
//   narrower           "Report" and "Test" tabs over one pane. Selecting a value shows the report
//                      (useVerificationView.select from 'value'); selecting a box in the report
//                      shows the Test, scrolled to the value
//   no view            the Test alone, exactly as the form has always drawn
//
// NOTHING IS MOUNTED AGAIN. The Test is always the same element in the same place, whether the
// view is open, and at whatever width, so a form that switches into the view mid-session (a report
// uploaded on it) keeps everything typed, every table's page and every open editor. Below 1200px
// both panes stay laid out in the same grid cell at full width, the one not shown hidden, so the
// report keeps its scroll, its zoom and its drawing, and the Test its place.
//
// The panes fill the height of the element this is in. In the full-screen Test dialog
// (CustomDialog `fullScreen`) that is the dialog's: give it a parent with a height.
//
// EACH PANE PLACES WHAT IT HOLDS. Text for a screen reader only (UnsavedChangeNote, the genes
// tested table's "Actions" header) is absolutely positioned, so it is placed against its nearest
// positioned ancestor. With none nearer, that was the dialog's own box, outside every scrolling
// pane: the dialog scrolled a second time, onto as much empty space as the form is tall, beside the
// 850px Test and below the full-screen view. Both panes are positioned, the Test's with no view too.
//
// FOCUS GOES WITH THE SELECTION BELOW 1200px (review of the view). A selection switches the tab,
// and the control just used ("Show in the report" beside a value, or a box in the report) is then
// in the pane made inert, so focus fell to the page and the dialog's root: the both-ways selection
// could not be followed from the keyboard. Where focus is lost so, it moves to the pane now shown:
// the report itself, or the Test value the box is about.
import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PANES } from './useVerificationView';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export const WIDE_QUERY = '(min-width:1200px)';
// How long the Test pane keeps looking for a value to scroll to: a gene row on another page of
// its table is drawn only once the table has moved to it.
const FIND_TRIES = 10;
const FIND_EVERY_MS = 30;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Positioned: see EACH PANE PLACES WHAT IT HOLDS.
const PLACES_SX = { position: 'relative' };
const PANE_SX = { ...PLACES_SX, minWidth: 0, minHeight: 0 };

// Focus moved to an element by script, made focusable by script only where it is not already.
function focusOn(el) {
  if (!el || typeof el.focus !== 'function') return;
  if (
    !el.hasAttribute('tabindex') &&
    !el.matches?.('a, button, input, select, textarea')
  ) {
    el.setAttribute('tabindex', '-1');
  }
  el.focus({ preventScroll: true });
}

export default function VerificationLayout({ view, report, children }) {
  const t = useAutoTranslation();

  const wide = useMediaQuery(WIDE_QUERY, { noSsr: true });
  const active = !!(view && view.active);
  const tabs = active && !wide;
  const testRef = React.useRef(null);
  const reportRef = React.useRef(null);
  const tabsRef = React.useRef(tabs);
  tabsRef.current = tabs;
  const ids = React.useId();
  const tabId = (pane) => `${ids}-tab-${pane}`;
  const paneId = (pane) => `${ids}-pane-${pane}`;

  // Focus lost to a pane made hidden (or to the page): below 1200px only, where a pane hides.
  const focusLostTo = (hidden) => {
    if (!tabsRef.current || typeof document === 'undefined') return false;
    const el = document.activeElement;
    return !el || el === document.body || !!hidden?.contains(el);
  };

  // A box selected in the report: the Test pane scrolls to the first value it is about, and below
  // 1200px takes the focus the report pane lost.
  const request = active ? view.focusRequest : null;
  React.useEffect(() => {
    if (!request) return undefined;
    const wanted = new Set(request.targets || []);
    let cancelled = false;
    let timer = null;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      const root = testRef.current;
      const found = root
        ? Array.from(root.querySelectorAll('[data-verification-target]')).find(
            (el) => wanted.has(el.getAttribute('data-verification-target'))
          )
        : null;
      if (found) {
        if (typeof found.scrollIntoView === 'function') {
          found.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        }
        if (focusLostTo(reportRef.current)) focusOn(found);
        return;
      }
      tries += 1;
      if (tries < FIND_TRIES) timer = setTimeout(attempt, FIND_EVERY_MS);
    };
    attempt();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // focusLostTo reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // A value shown in the report below 1200px: the report tab takes the focus the Test pane lost,
  // in the report itself (its scrolling region, which the keyboard reads and scrolls).
  const pane = active ? view.pane : null;
  const lastPane = React.useRef(pane);
  React.useEffect(() => {
    const was = lastPane.current;
    lastPane.current = pane;
    if (!tabs || was === pane || pane !== PANES.report) return;
    if (!focusLostTo(testRef.current)) return;
    const root = reportRef.current;
    focusOn(root?.querySelector('[role="region"]') || root);
    // focusLostTo reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, pane]);

  const shown = (pane) => !tabs || view.pane === pane;
  // A hidden pane is out of sight, out of the tab order and out of the accessibility tree, and
  // keeps its size, so nothing in it is laid out again when it is shown.
  const paneProps = (pane) =>
    tabs
      ? {
          role: 'tabpanel',
          id: paneId(pane),
          'aria-labelledby': tabId(pane),
          ...(shown(pane)
            ? {}
            : {
                'aria-hidden': true,
                inert: true,
                style: { visibility: 'hidden', pointerEvents: 'none' }
              })
        }
      : {};

  const rootSx = !active
    ? undefined
    : {
        display: 'grid',
        flex: '1 1 auto',
        minHeight: 0,
        minWidth: 0,
        ...(tabs
          ? {
              gridTemplateColumns: 'minmax(0, 1fr)',
              gridTemplateRows: 'auto minmax(0, 1fr)'
            }
          : {
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gridTemplateRows: 'minmax(0, 1fr)',
              columnGap: 2
            })
      };

  return (
    <Box
      data-testid="verification-layout"
      data-layout={!active ? 'form' : tabs ? 'tabs' : 'side-by-side'}
      sx={rootSx}
    >
      {tabs && (
        <Tabs
          key="tabs"
          value={view.pane}
          onChange={(_, pane) => view.setPane(pane)}
          aria-label={t('The report and the Test')}
          variant="fullWidth"
          sx={{
            gridColumn: 1,
            gridRow: 1,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 1
          }}
        >
          <Tab
            value={PANES.report}
            label={t('Report')}
            id={tabId(PANES.report)}
            aria-controls={paneId(PANES.report)}
          />
          <Tab
            value={PANES.test}
            label={t('Test')}
            id={tabId(PANES.test)}
            aria-controls={paneId(PANES.test)}
          />
        </Tabs>
      )}
      {active && (
        <Box
          key="report"
          ref={reportRef}
          data-testid="verification-report-pane"
          data-shown={shown(PANES.report) ? 'true' : 'false'}
          {...paneProps(PANES.report)}
          sx={{
            ...PANE_SX,
            gridColumn: 1,
            gridRow: tabs ? 2 : 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          {report}
        </Box>
      )}
      <Box
        key="test"
        ref={testRef}
        data-testid="verification-test-pane"
        data-shown={!active || shown(PANES.test) ? 'true' : 'false'}
        {...paneProps(PANES.test)}
        sx={
          active
            ? {
                ...PANE_SX,
                gridColumn: tabs ? 1 : 2,
                gridRow: tabs ? 2 : 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1
              }
            : PLACES_SX
        }
      >
        {children}
      </Box>
    </Box>
  );
}
